import { useState } from "react";
import { CONSOLE, Client, TOOLS, apiBase , trackKeyCreated } from "@authoxi/ui-shared";

const client = new Client({ baseUrl: apiBase() });

interface Tenant {
  tenant_id: string;
  publishable_key: string;
  secret_key: string;
  status: string;
}

/**
 * Get a key — the only page on the marketing site that does real work.
 *
 * `POST /signup` takes **no credential at all** and returns an ordinary tenant. Not a trial,
 * not a sandbox: a parallel restricted tenancy would be a second product to keep correct, with
 * all the bugs living in the seam between them.
 *
 * The secret is shown **once**, and the warning next to it is not boilerplate. `sk_live_` can
 * ISSUE authority — mint mandates, register agents. An agent holds a *passport*, which can only
 * ask. An agent that could issue its own mandate would not be governed by one, and the fastest
 * way to get there is to paste the wrong key into a deployment.
 */
export function GetKey() {
  const [name, setName] = useState("");
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  return (
    <section className="section">
      <div className="wide">
        <div className="rule">
          <span className="label">Get started</span>
        </div>
        <div className="qs">
          <div>
            <h1 className="xl" style={{ marginBottom: "var(--s4)" }}>
              You can have a key before you finish reading this.
            </h1>
            <p className="lede">
              No credential, no card, no call. <span className="mono">POST /signup</span> returns a
              tenant and a live key. The first thing in the quickstart used to be “email us”,
              which is a strange thing to ask of somebody evaluating an access-control product.
            </p>

            {!tenant && (
              <form
                className="tool-input"
                style={{ marginTop: "var(--s5)" }}
                data-testid="signup-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  setBusy(true);
                  setErr(null);
                  try {
                    const created = await client.post<Tenant>("/signup", { name: name.trim() });
                    setTenant(created);
                    // The end of the funnel this product actually has. No tenant name, no key —
                    // only that one was issued, and from where.
                    trackKeyCreated("landing", "/start");
                  } catch (e) {
                    setErr(
                      e instanceof Error ? e.message : "That did not work. Try again in a moment.",
                    );
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <input
                  className="input"
                  required
                  minLength={2}
                  placeholder="Your company"
                  aria-label="Company name"
                  data-testid="signup-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <button className="btn btn-go btn-lg" type="submit" disabled={busy}>
                  {busy ? "…" : "Get a key"}
                </button>
              </form>
            )}

            {err && (
              <p className="sm" style={{ color: "var(--deny-text)", marginTop: "var(--s3)" }}>
                {err}
              </p>
            )}

            {tenant && (
              <div className="card pad-lg" style={{ marginTop: "var(--s5)" }} data-testid="signup-result">
                <p className="label">Your secret key — shown once</p>
                <pre className="code" style={{ marginTop: "var(--s3)" }} data-testid="secret-key">
                  {tenant.secret_key}
                </pre>
                <div className="row" style={{ marginTop: "var(--s3)" }}>
                  <button
                    className="btn"
                    onClick={() => {
                      void navigator.clipboard?.writeText(tenant.secret_key);
                      setCopied(true);
                    }}
                  >
                    {copied ? "Copied" : "Copy"}
                  </button>
                  <a className="btn btn-go" href={CONSOLE} rel="external">
                    Open the console
                  </a>
                </div>

                {/* The distinction that matters more than any other sentence on this page. */}
                <div className="hr" />
                <p className="label" style={{ color: "var(--deny-text)" }}>
                  This key can ISSUE authority
                </p>
                <p className="sm" style={{ margin: "var(--s2) 0 0", color: "var(--ink-2)", maxWidth: "var(--measure)" }}>
                  It mints mandates and registers agents. It belongs on your server and{" "}
                  <strong>never inside an agent</strong> — an agent holds a passport, which can
                  only ask. An agent that could issue its own mandate would not be governed by
                  one. Nothing can recover this key: rotate if you lose it.
                </p>
                <dl className="kv" style={{ marginTop: "var(--s4)" }}>
                  <dt>tenant</dt>
                  <dd className="mono">{tenant.tenant_id}</dd>
                  <dt>publishable</dt>
                  <dd className="mono">{tenant.publishable_key}</dd>
                </dl>
              </div>
            )}

            <div className="cta-row" style={{ marginTop: "var(--s5)" }}>
              <a className="btn btn-lg" href="/docs">
                Read the quickstart
              </a>
              <a className="btn btn-lg" href={`${TOOLS}/grader`}>
                Grade an agent first
              </a>
            </div>
          </div>

          <pre className="code">{`curl -X POST https://api.authoxi.com/signup \\
  -H 'content-type: application/json' \\
  -d '{"name": "Acme Robotics"}'

# → {
#     "tenant_id": "ten_…",
#     "publishable_key": "pk_live_…",
#     "secret_key": "sk_live_…"
#   }`}</pre>
        </div>
      </div>
    </section>
  );
}
